import Button from '@components/Button';
import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import Header from '@components/Header';
import TextField from '@components/TextField';
import styled from '@emotion/styled';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BarcodeExample from './components/BarcodeExample';
import { isValidGiftCode } from '../_shared/giftCode';
import useRegisterGift from '../_shared/useRegisterGift';

declare module '@stackflow/config' {
    interface Register {
        RegisterGiftActivity: {};
    }
}

const CODE_INPUT_ID = 'gift-code-input';

const RegisterGiftActivity: ActivityComponentType<'RegisterGiftActivity'> = () => {
    const { t } = useTranslation('gift');
    const { pop } = useFlow();
    const [code, setCode] = useState('');
    const [errorText, setErrorText] = useState('');
    const { register, isPending } = useRegisterGift(() => pop(), setErrorText);

    useEffect(() => {
        const timeout = setTimeout(() => {
            document.getElementById(CODE_INPUT_ID)?.focus({ preventScroll: true });
        }, 300);
        return () => clearTimeout(timeout);
    }, []);

    return (
        <AppScreen>
            <CtaLayout noFooterBorder
                header={<Header title={t('newGift.barcodeTitle')} />}
                footer={(
                    <Button fullWidth disabled={isPending || !isValidGiftCode(code)}
                        onClick={() => register(code)}>
                        {t('newGift.register')}
                    </Button>
                )}>
                <Container fullWidth spacing={4}>
                    <BarcodeExample />
                    <TextField id={CODE_INPUT_ID}
                        inputMode='decimal'
                        autoComplete='off'
                        aria-label={t('newGift.barcodeTitle')}
                        placeholder={t('newGift.barcodePlaceholder')}
                        maxLength={16}
                        value={code}
                        disabled={isPending}
                        errorText={errorText}
                        onChange={(event) => {
                            setCode(event.target.value.replace(/\D/g, ''));
                            setErrorText('');
                        }} />
                </Container>
            </CtaLayout>
        </AppScreen>
    );
};

const Container = styled(Column)`
    padding: var(--spacing-6) var(--spacing-4) var(--spacing-10);
`;

export default RegisterGiftActivity;
